import { ZoneAnalysisAnchorSchema } from 'features/libra/stepComponents/zoneAnalysis/zoneCommentAnchors'
import { z } from 'zod'

export type ThreadId = string & { readonly __brand: 'ThreadId' }
export type StepId = string & { readonly __brand: 'StepId' }

export const FieldAnchorSchema = z.object({
  type: z.literal('field'),
  stepId: z.string(),
  fieldId: z.string(),
  /**
   * Set when the field is component-scoped (rendered inside
   * `<UnitComponentTabs>` against `fieldValues.components[componentId]`).
   * Absent for ticket-global fields. Two anchors with the same `fieldId`
   * but different `componentId` are DIFFERENT anchors (two comment
   * threads) — see `getAnchorKey`.
   */
  componentId: z.string().optional(),
})

export const CellAnchorSchema = z.object({
  type: z.literal('cell'),
  stepId: z.string(),
  fieldId: z.string(),
  rowId: z.string(),
  columnKey: z.string(),
  /** See `FieldAnchorSchema.componentId`. */
  componentId: z.string().optional(),
})

export const FlowchartAnchorSchema = z.object({
  type: z.literal('flowchart'),
  stepId: z.string(),
  sectionId: z.string(),
  nodeId: z.string().optional(),
})

export const ComputedAnchorSchema = z.object({
  type: z.literal('computed'),
  stepId: z.string(),
  sectionId: z.string(),
})

export const SectionAnchorSchema = z.object({
  type: z.literal('section'),
  stepId: z.string(),
  sectionId: z.string(),
})

export const StepAnchorSchema = z.object({
  type: z.literal('step'),
  stepId: z.string(),
})

export const CoreAnchorSchema = z.discriminatedUnion('type', [
  FieldAnchorSchema,
  CellAnchorSchema,
  FlowchartAnchorSchema,
  ComputedAnchorSchema,
  SectionAnchorSchema,
  StepAnchorSchema,
])

export const CommentAnchorSchema = z.union([
  CoreAnchorSchema,
  ZoneAnalysisAnchorSchema,
])

export type CommentAnchor = z.infer<typeof CommentAnchorSchema>

/**
 * Canonical key for an anchor, independent of property ordering.
 * Use this instead of raw JSON.stringify(anchor) for map keys and lookups.
 */
export function getAnchorKey(anchor: CommentAnchor): string {
  return JSON.stringify(anchor, Object.keys(anchor).sort())
}

export const CommentEntryDataSchema = z.object({
  author: z.string(),
  authorName: z.string(),
  text: z.string(),
  createdAt: z.string(),
})

export interface Comment extends z.infer<typeof CommentEntryDataSchema> {
  id: string
}

export interface CommentWithThread extends Comment {
  threadId: string
}

export const CommentThreadDataSchema = z.object({
  anchor: CommentAnchorSchema,
  resolved: z.boolean().default(false),
  resolvedBy: z.string().optional(),
  resolvedByName: z.string().optional(),
  resolvedAt: z.string().optional(),
  valueAtCreation: z.unknown().optional(),
  users: z.array(z.string()).default([]),
})

export type CommentThreadData = z.infer<typeof CommentThreadDataSchema>

export interface CommentThread extends CommentThreadData {
  entryId: ThreadId
  comments: Comment[]
}
